import React, { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import logo from "../../../assets/images/others/host-side-images/logo.png";
import { ReactComponent as GoogleIcon } from "../../../assets/images/svgs/google-icon.svg";
import { ReactComponent as FacebookIcon } from "../../../assets/images/svgs/facebook-icon.svg";
import { ReactComponent as AppleIcon } from "../../../assets/images/svgs/apple-icon.svg";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "../../../utils/helpers/enums/routes";
import { LoginFormDTO } from "../../../utils/helpers/models/auth/login";
import useAuth from "../useHooks";
import { errorToaster } from "../../../utils/helpers/common/alert-service";
import useOutsideClick from "../../../hooks/click-outside";
import { LoginByGoogleDTO } from "utils/helpers/models/auth/login-by-google.dto";
import { LoginByAppleDTO } from "utils/helpers/models/auth/login-by-apple.dto";
import {
  signInWithPopup,
  auth,
  googleProvider,
  appleProvider,
} from "../../../firebase";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, resendOTP } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const { loginByGoogle, loginByApple } = useAuth();
  const [googleLoginForm, setGoogleLoginForm] = useState<LoginByGoogleDTO>(
    new LoginByGoogleDTO()
  );
  const [appleLoginForm, setAppleLoginForm] = useState<LoginByAppleDTO>(
    new LoginByAppleDTO()
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<LoginFormDTO>();

  const email = watch("email");

  // const handleResend = async () => {
  //   try {
  //     await resendOTP({ email });
  //   } catch (error) {
  //     console.error("Error resending OTP:", error);
  //   }
  // };

  const onSubmit: SubmitHandler<LoginFormDTO> = (data: any) => {
    console.log("Form Data:", data);
    login(data);
  };

  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const { _tokenResponse = null } = (result as any) || {};
      if (_tokenResponse) {
        const { oauthAccessToken: accessToken } = _tokenResponse as any;
        if (accessToken) {
          googleLoginForm.accessToken = accessToken;
          setGoogleLoginForm(googleLoginForm);
          loginByGoogle(googleLoginForm);
        }
      }
    } catch (error) {
      console.error("Error logging in with Google:", error);
    }
  };

  const handleAppleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, appleProvider);
      const user = result.user;

      // Extract user information from Apple sign-in
      const displayName = user.displayName || "";
      const nameParts = displayName.split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      const appleData = new LoginByAppleDTO({
        firstName: firstName,
        lastName: lastName,
        email: user.email || "",
        uid: user.uid,
        role: appleLoginForm.role,
        profileType: appleLoginForm.profileType,
      });

      setAppleLoginForm(appleData);
      loginByApple(appleData);
    } catch (error) {
      console.error("Error logging in with Apple:", error);
    }
  };

  return (
    <div className="w-full h-screen overflow-y-auto flex flex-col p-4 sm:p-6 lg:px-8 xl:px-10 lg:py-6">
      <div className="flex justify-center my-6">
        <img src={logo} alt="logo" className="h-20" />
      </div>
      <div className="flex-1 flex justify-center">
        <div className="w-full">
          <h1 className="text-3xl font-medium text-blackie mb-4 text-start">
            Continue with Username or Email
          </h1>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            {/* Email Input */}
            <div className="flex flex-col gap-1">
              <label className="custom-label">Username or email address</label>

              <input
                type="text"
                placeholder="Enter your Username or Email"
                {...register("email", {
                  required: "Username/Email is required",
                  setValueAs: (v) => (typeof v === 'string' ? v.trim() : v),
                  // pattern: {
                  //   value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                  //   message: "Please enter a valid email address",
                  // },
                })}
                className="custom-input"
              />

              {errors.email && (
                <span className="text-red-500 text-sm">
                  {errors.email.message}
                </span>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="custom-label">Password</label>
                <div
                  className="cursor-pointer text-sm flex items-center text-label"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <>
                      <AiFillEyeInvisible size={18} className="mr-1" />
                      <span>Hide</span>
                    </>
                  ) : (
                    <>
                      <AiFillEye size={18} className="mr-1" />
                      <span>Show</span>
                    </>
                  )}
                </div>
              </div>

              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                {...register("password", {
                  required: "Password is required",
                  setValueAs: (v) => (typeof v === 'string' ? v.trim() : v),
                })}
                className="custom-input"
              />

              <div className="flex justify-between">
                {errors.password && (
                  <span className="text-red-500 text-sm">
                    {errors.password.message}
                  </span>
                )}
                <div className="flex justify-end">
                  <span
                    onClick={async () => {
                      if (!email) {
                        errorToaster(
                          "Please enter your email before proceeding to the forgot password screen."
                        );
                      } else {
                        // Check if email is valid
                        // const emailPattern =
                        //   /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
                        if (email) {
                          await resendOTP({ email });
                        } else {
                          errorToaster("Please enter a valid email address.");
                        }
                      }
                    }}
                    className="text-base cursor-pointer text-primaryBlueText"
                  >
                    Forgot Password?
                  </span>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="">
              <button
                type="submit"
                className="lg:h-[56px] p-3 mt-2 text-xl bg-primaryBlueBg rounded-full text-white w-full"
              >
                Sign in
              </button>
            </div>
          </form>
          <div>
            <h1 className="text-xl text-center font-medium text-labelTextColor my-4 ">
              OR
            </h1>
            <div className="flex flex-col gap-4 w-full text-blackie">
              <button
                onClick={handleGoogleLogin}
                className="w-full lg:h-[64px] flex items-center p-3  gap-2 justify-center border border-borderColor hover:border- rounded-full px-6 text-blackie text-lg hover:bg-fade hover:text-blueText transition-all duration-300 ease-in-out"
              >
                <GoogleIcon className="w-6 h-6" />
                <span className="text-blackie">Continue with Google</span>
              </button>
              {/* <button
                onClick={() => setShowLaunchingSoonModal(true)}
                className="w-full p-3 lg:h-[64px] flex items-center  gap-2 justify-center border border-borderColor hover:border rounded-full px-6 text-blackie text-lg hover:bg-fade hover:text-yellowText transition-all duration-300 ease-in-out"
              >
                <FacebookIcon className="w-6 h-6" />
                <span className="text-blackie">Continue with Facebook</span>
              </button> */}
              <button
                onClick={handleAppleLogin}
                className="w-full p-3 lg:h-[64px] flex items-center  gap-2 justify-center border border-borderColor hover:border rounded-full px-6 text-blackie text-lg hover:bg-fade hover:text-purpleText transition-all duration-300 ease-in-out"
              >
                <AppleIcon className="w-6 h-6" />
                <span className="text-blackie">Continue with Apple ID</span>
              </button>
            </div>
          </div>
          <div className="text-center mt-4">
            <p className="text-blackie text-base">
              Don’t have an Account?
              <span
                onClick={() => {
                  navigate(siteRoutes.loginType);
                }}
                className="text-yellowText cursor-pointer ml-[3.5px]"
              >
                Sign up
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
